import React, { Fragment, useState } from "react";

import { LogOut } from "lucide-react";

import type EncounterPatientSerializer from "issara-sdk/types/EncounterPatientSerializer_core";
import type EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import type PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";

import { calculateAgeAndDOB } from "react-lib/apps/HISV3/common/CommonInterface";

import { stageOf } from "../common/commonV4Interface";
import { flagsOf, numberText, type FlagSource, type PatientFlag } from "./patientPanelModelV4";
import { AlertV4, ButtonV4, ModalV4 } from "../common/ui";
import {
  canCancelCheckout,
  canCheckout,
  checkoutEncounterFor,
  checkoutInfoOf,
  type CheckoutCause,
  type CheckoutEncounter,
} from "./checkoutModelV4";
import ModalCancelCheckoutV4 from "./ModalCancelCheckoutV4";
import ModalCheckoutV4 from "./ModalCheckoutV4";
import type { PresencePanelV4Binding } from "../presence/presenceTypesV4";
import type { TeamMessageRecord } from "../registration/registrationModelV4";
import type { MemberInfo } from "./examModelV4";
import { genderText } from "../chat/chatModelV4";

// shell ของทุกหน้าใน Encounter: ข้อมูลผู้ป่วย flag และเมนู 3 กลุ่ม

export type PatientMenuKey =
  | "history"
  | "package"
  | "docs"
  | "screen"
  | "doctor"
  | "orders"
  | "results"
  | "tele"
  | "billing"
  | "gallery"
  | "nursing"
  | "medcert";

export type { FlagKey, PatientFlag } from "./patientPanelModelV4";

export type PatientPanelV4Props = {
  // data
  patient?: PatientSerializer | null;
  /** selectedEncounter หรือแถวคิว (ก่อนโหลดเสร็จ) */
  encounter?: EncounterSerializer | EncounterPatientSerializer | null;
  nextAppointment?: string;
  patientADR?: any;
  problemListDPO?: any;
  /** ปุ่มเปิด editor ของประวัติแพ้ / โรคประจำตัว */
  onEditFlags?: () => void;
  /** ระดับสมาชิก (null = ยังไม่เป็นสมาชิก, undefined = ยังโหลดไม่เสร็จ) */
  member?: MemberInfo | null;
  /** ช่องทางที่รู้จักคลินิก / ยาที่ใช้ประจำ ของ encounter */
  channels?: string[];
  latestChannel?: TeamMessageRecord;
  specialRequests?: TeamMessageRecord;
  teamMessageErrors?: string[];
  medications?: string[];
  /** สิทธิการรักษาจาก encounter detail (แถวคิวไม่มี coverage) */
  coverage?: string;
  activeMenu: PatientMenuKey;
  onSelectMenu: (menu: PatientMenuKey) => void;
  onBackToQueue: () => void;
  /**
   * แถบ "หน้านี้มีผู้อื่นกำลังใช้งานอยู่" + จับช่องที่ผู้ใช้ focus/พิมพ์ ส่งให้ presence client
   */
  presence?: PresencePanelV4Binding;
  /** Check out ของ Encounter (ไม่ส่งมา = ไม่แสดงปุ่ม) */
  checkout?: PatientCheckoutBinding;
  children: React.ReactNode;
};

export type PatientCheckoutBinding = {
  /** ฟิลด์ Check out จาก encounter detail */
  encounter?: CheckoutEncounter | null;
  saving?: boolean;
  error?: unknown;
  onCheckout: (
    cause: CheckoutCause,
    note: string,
    deathDatetime: string,
    onDone: (ok: boolean) => void,
  ) => void;
  onCancel: (onDone: (ok: boolean) => void) => void;
};

/* แบบ 4d ของหน้า Patient History Viewer ใช้ gap 14 ใน main (หน้าอื่นของ workspace ใช้ 16)
   วัดจาก docs/design/PatientHistory.html -> ใส่ modifier แทนการใส่ <main> ซ้ำในตัว card */
const MAIN_GAP_14: PatientMenuKey[] = ["history"];

const mainClassNameOf = (menu: PatientMenuKey) =>
  ["hv4-main", ...(MAIN_GAP_14.includes(menu) ? ["hv4-main--gap14"] : [])].join(" ");

type MenuItem = {
  key: PatientMenuKey;
  title: string;
  subtitle: string;
  icon: string;
};

type MenuGroup = { label: string; items: MenuItem[] };

export const PATIENT_MENU_GROUPS: MenuGroup[] = [
  {
    label: "ข้อมูลผู้ป่วย",
    items: [
      {
        key: "history",
        title: "Patient History Viewer",
        subtitle: "ประวัติการรักษาทุกครั้ง",
        icon: "🕘",
      },
      {
        key: "package",
        title: "Package",
        subtitle: "แพ็กเกจที่เปิดขาย · ที่เลือก · ประวัติใช้งาน",
        icon: "📦",
      },
      {
        key: "docs",
        title: "Document",
        subtitle: "Print list · เอกสารสแกน · ใบรับรองแพทย์",
        icon: "📄",
      },
    ],
  },
  {
    label: "งานบริการ",
    items: [
      {
        key: "screen",
        title: "คัดกรอง",
        subtitle: "อาการนำ · ประวัติสุขภาพ · Vital Sign",
        icon: "🩺",
      },
      {
        key: "doctor",
        title: "ตรวจรักษา",
        subtitle: "ประวัติ · ตรวจร่างกาย · Dx · Image Gallery · บันทึกการพยาบาล",
        icon: "⚕️",
      },
      {
        key: "orders",
        title: "คำสั่ง",
        subtitle: "แลป · Imaging · หัตถการ · ยา · เวชภัณฑ์ · เบ็ดเตล็ด · Doctor Fee",
        icon: "📝",
      },
      { key: "results", title: "Result Summary", subtitle: "ผลแลป · ผล Imaging", icon: "🧪" },
      {
        key: "tele",
        title: "TeleHealth",
        subtitle: "วิดีโอ · แชท · ไฟล์จากผู้รับบริการ",
        icon: "📹",
      },
    ],
  },
  {
    label: "การเงิน",
    items: [
      {
        key: "billing",
        title: "สรุปค่าใช้จ่าย",
        subtitle: "ตรวจรายการ · รับชำระเงิน · Wallet",
        icon: "💳",
      },
    ],
  },
];

/** Image Gallery (6c) และ บันทึกการพยาบาล (6b) เป็นหน้าลูกของ ตรวจรักษา */
export const PATIENT_SUBPAGES: MenuItem[] = [
  { key: "gallery", title: "Image Gallery", subtitle: "ภาพการตรวจ · ก่อน-หลังหัตถการ", icon: "🖼️" },
  {
    key: "nursing",
    title: "บันทึกการพยาบาล",
    subtitle: "Nurse Note · Multidisciplinary",
    icon: "🩹",
  },
  {
    key: "medcert",
    title: "ใบรับรองแพทย์",
    subtitle: "ออกใบรับรอง · ประวัติการออก",
    icon: "📜",
  },
];

const ALL_MENU_ITEMS: MenuItem[] = [
  ...PATIENT_MENU_GROUPS.flatMap((group) => group.items),
  ...PATIENT_SUBPAGES,
];

const PARENT_OF: Partial<Record<PatientMenuKey, PatientMenuKey>> = {
  gallery: "doctor",
  nursing: "doctor",
  medcert: "docs",
};

export function patientMenuTitle(key: PatientMenuKey): string {
  const found = ALL_MENU_ITEMS.find((item) => item.key === key);
  return found === undefined ? key : found.title;
}

export const PinIcon = (props: { pinned: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill={props.pinned ? "#B6400C" : "none"}
    stroke={props.pinned ? "#B6400C" : "#8C9BA0"}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 3h6l-1 6 3 3v2H7v-2l3-3-1-6z" />
    <path d="M12 14v7" />
  </svg>
);

const PHOTO_CAPTION: Record<"P" | "I", string> = { I: "รูปจากบัตรประชาชน", P: "รูปถ่าย" };

const MemberLink = () => (
  // หน้า สิทธิการรักษา / สมาชิก ยังไม่มีใน V4
  <button type="button" className="hv4-mem-link" disabled title="ยังไม่เปิดใช้งาน">
    ดูสิทธิสมาชิก →
  </button>
);

const MemberDetail = (props: { member: MemberInfo | null }) => {
  const { member } = props;
  if (!member) {
    return (
      <div className="hv4-mem-detail">
        <div className="hv4-mem-value">ยังไม่เป็นสมาชิก</div>
        <MemberLink />
      </div>
    );
  }
  return (
    <div className="hv4-mem-detail">
      <span className={`hv4-mem-tier hv4-mem-tier-${member.tierIndex}`}>🏅 {member.tier}</span>
      <div className="hv4-mem-no">เลขสมาชิก {member.id}</div>
      <div className="hv4-mem-points">คะแนนคงเหลือ {numberText(member.points)} คะแนน</div>
      {member.soonPoints > 0 && (
        <div className="hv4-mem-expire">
          ⚠ {numberText(member.soonPoints)} คะแนนหมดอายุ {member.soonDate}
        </div>
      )}
      <div className="hv4-mem-spend">ยอดซื้อสะสม ฿{numberText(member.spend)}</div>
      {member.nextTier ? (
        <>
          <div className="hv4-mem-need">
            อีก ฿{numberText(member.need)} ถึงระดับ {member.nextTier}
          </div>
          <div className="hv4-mem-bar">
            <div className="hv4-mem-bar-fill" style={{ width: `${member.percent}%` }} />
          </div>
        </>
      ) : (
        <div className="hv4-mem-top">ระดับสูงสุดแล้ว</div>
      )}
      <MemberLink />
    </div>
  );
};

export const PatientPhoto = (props: { src?: string }) => {
  const [failedSrc, setFailedSrc] = useState("");

  return (
    <div className="hv4-photo">
      {props.src && props.src !== failedSrc ? (
        <img src={props.src} alt="" onError={() => setFailedSrc(props.src || "")} />
      ) : (
        <svg width="50" height="62" viewBox="0 0 64 80" fill="#9CB0B8" aria-hidden="true">
          <circle cx="32" cy="24" r="16" />
          <path d="M4 80c2-22 14-30 28-30s26 8 28 30z" />
        </svg>
      )}
    </div>
  );
};

type FlagButtonsProps = {
  flags: PatientFlag[];
  openKey: PatientFlag["key"] | null;
  onToggle: (key: PatientFlag["key"]) => void;
};

const FlagButtons = (props: FlagButtonsProps) => (
  <div role="group" aria-label="ข้อความสื่อสารถึงทีม" className="hv4-flag-row">
    {props.flags.length === 0 && <span className="hv4-flag-empty">ยังไม่มีข้อความถึงทีม</span>}
    {props.flags.map((flag) => {
      const on = props.openKey === flag.key;
      const classes = [
        "hv4-flag-btn",
        `hv4-flag-btn-${flag.key}`,
        flag.glyph.length > 1 ? "hv4-flag-btn-glyph-long" : "",
        flag.off ? "hv4-flag-btn-off" : "",
        on ? "hv4-flag-btn-on" : "",
      ]
        .filter(Boolean)
        .join(" ");
      return (
        <button
          key={flag.key}
          type="button"
          className={classes}
          aria-label={flag.label}
          aria-pressed={on}
          title={flag.label}
          onClick={() => props.onToggle(flag.key)}
        >
          {flag.glyph}
        </button>
      );
    })}
  </div>
);

/** Same flags in the encounter workspace and registration sidebar. */
export const PatientTeamFlagsV4 = (props: FlagSource) => {
  const [openFlagKey, setOpenFlagKey] = useState<PatientFlag["key"] | null>(null);
  const flags = flagsOf(props);
  const openFlag = flags.find((flag) => flag.key === openFlagKey) ?? null;
  return <>
        <FlagButtons
          flags={flags}
          openKey={openFlagKey}
          onToggle={(key) => setOpenFlagKey((c) => (c === key ? null : key))}
        />

        {openFlag !== null && (
          <div role="status" className="hv4-info-card hv4-flag-card-text">
            <div className="hv4-flag-card-label">{openFlag.label}</div>
            <div>{openFlag.text}</div>
            <div className="hv4-flag-card-note">
              บันทึกโดย {openFlag.recordedBy || openFlag.adr?.edit_user || "-"} · {openFlag.recordedAt || openFlag.adr?.edited || "-"}
            </div>
            {/* editor ของประวัติแพ้ / โรคประจำตัว */}
            {props.onEditFlags && openFlag.key === "alg" && (
              <ButtonV4 variant="ghost" size="sm" wrap onClick={props.onEditFlags}>
                เพิ่ม / แก้ / ปิดใช้งานประวัติแพ้
              </ButtonV4>
            )}
            {props.onEditFlags && openFlag.key === "chr" && (
              <ButtonV4 variant="ghost" size="sm" wrap onClick={props.onEditFlags}>
                เพิ่ม / แก้ / ปิดใช้งานโรคประจำตัว
              </ButtonV4>
            )}
          </div>
        )}

  </>;
};

const PatientPanelV4 = (props: PatientPanelV4Props) => {

  const [collapsed, setCollapsed] = useState(false);
  const [pinned, setPinned] = useState<PatientMenuKey[]>([]);
  const [memberOpen, setMemberOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState<"checkout" | "cancel" | null>(null);
  const stage = stageOf(props.encounter);
  const checkoutEncounter = checkoutEncounterFor(
    props.encounter as CheckoutEncounter | null,
    props.checkout?.encounter,
  );
  const checkoutInfo = props.checkout ? checkoutInfoOf(checkoutEncounter) : null;
  const showCheckout = !!props.checkout && canCheckout(checkoutEncounter);
  const photo = props.patient?.profile_image;
  const current = PARENT_OF[props.activeMenu] ?? props.activeMenu;
  const rows: { key: string; value?: string | null }[] = [
    { key: "เพศ", value: genderText(props.patient?.gender) || props.patient?.gender_name },
    { key: "อายุ", value: calculateAgeAndDOB(props.patient?.full_age).fullage_th },
    {
      key: "วันเกิด",
      value: props.patient?.birthdate === "01/01/1970" ? "" : props.patient?.birthdate,
    },
    { key: "เบอร์โทร", value: props.patient?.present_address?.tel_mobile },
    { key: "Citizen ID", value: props.patient?.citizen_passport || props.patient?.citizen_no },
    {
      key: "สิทธิ",
      value:
        props.coverage ||
        (props.encounter && "coverage" in props.encounter ? props.encounter.coverage : ""),
    },
    { key: "นัดครั้งหน้า", value: props.nextAppointment },
  ];

  const togglePin = (key: PatientMenuKey): void =>
    setPinned((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]));

  // presence: focus = อยู่ในหน้า, change (capture) = ช่องที่กำลังพิมพ์
  const presenceFocus = props.presence
    ? (): void => {
        props.presence?.setActive();
      }
    : undefined;
  const presenceTyping = props.presence
    ? (event: React.FormEvent<HTMLElement>): void => {
        const id = String((event.target as HTMLElement | null)?.id ?? "").trim();
        if (id) props.presence?.setTyping(id);
      }
    : undefined;

  const pinnedItems = ALL_MENU_ITEMS.filter((item) => pinned.includes(item.key));
  const groups = PATIENT_MENU_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter((item) => !pinned.includes(item.key)),
  })).filter((group) => group.items.length > 0);

  const renderRow = (item: MenuItem): React.ReactNode => {
    const on = item.key === current;
    const isPinned = pinned.includes(item.key);
    if (collapsed) {
      return (
        <button
          key={item.key}
          type="button"
          className={`hv4-menu-item hv4-menu-item-ico${on ? " hv4-menu-item-on" : ""}`}
          aria-current={on ? "page" : undefined}
          title={item.title}
          aria-label={item.title}
          onClick={() => props.onSelectMenu(item.key)}
        >
          <span aria-hidden="true">{item.icon}</span>
        </button>
      );
    }
    return (
      <div
        key={item.key}
        className={`hv4-menu-item${on ? " hv4-menu-item-on" : ""}`}
        aria-current={on ? "page" : undefined}
      >
        <button
          type="button"
          className="hv4-menu-link"
          onClick={() => props.onSelectMenu(item.key)}
        >
          <span className="hv4-menu-t1">{item.title}</span>
          <span className="hv4-menu-t2">{item.subtitle}</span>
        </button>
        <button
          type="button"
          className={`hv4-menu-pin${isPinned ? " hv4-menu-pin-on" : ""}`}
          aria-pressed={isPinned}
          aria-label={`${isPinned ? "เลิกปักหมุด " : "ปักหมุด "}${item.title}`}
          title={isPinned ? "เลิกปักหมุด" : "ปักหมุดเมนูนี้ไว้บนสุด"}
          onClick={() => togglePin(item.key)}
        >
          <PinIcon pinned={isPinned} />
        </button>
      </div>
    );
  };

  return (
    <div className="hv4 hv4-workspace">
      <aside aria-label="ข้อมูลผู้ป่วย" className="hv4-patient-panel">
        <button type="button" className="hv4-back-btn" onClick={props.onBackToQueue}>
          ← กลับไปคิววันนี้
        </button>

        <PatientPhoto src={photo?.image} />

        {photo && <div className="hv4-photo-caption">{PHOTO_CAPTION[photo.image_type]}</div>}

        <div role="group" aria-label="HN และชื่อผู้รับบริการ" className="hv4-id-card">
          <div className="hv4-id-label">HN</div>
          <div className="hv4-id-hn">{props.patient?.hn || props.encounter?.hn || "-"}</div>
          <div className="hv4-id-name">
            {props.patient?.full_name || props.encounter?.patient_name || "-"}
          </div>
        </div>

        {showCheckout && (
          <button
            type="button"
            className="hv4-co-btn"
            title="จบการรับบริการ เลือกรูปแบบการ Check out"
            onClick={() => setCheckoutOpen("checkout")}
          >
            <LogOut size={18} strokeWidth={2.2} aria-hidden="true" />
            Check out
          </button>
        )}

        {checkoutInfo !== null && (
          <div role="status" className="hv4-co-done">
            <b className="hv4-co-done-title">✓ Check out แล้ว</b>
            <span className="hv4-co-done-type">{checkoutInfo.typeText}</span>
            <span className="hv4-co-done-meta">{checkoutInfo.atText}</span>
            {!!checkoutInfo.note && (
              <span className="hv4-co-done-meta">หมายเหตุ: {checkoutInfo.note}</span>
            )}
            {canCancelCheckout(checkoutEncounter) && (
              <button
                type="button"
                className="hv4-co-undo"
                onClick={() => setCheckoutOpen("cancel")}
              >
                ยกเลิก Check out
              </button>
            )}
          </div>
        )}

        {checkoutOpen === "checkout" && props.checkout && (
          <ModalCheckoutV4
            saving={props.checkout.saving}
            error={props.checkout.error}
            onConfirm={(cause, note, deathDatetime) =>
              props.checkout?.onCheckout(
                cause,
                note,
                deathDatetime,
                (ok) => ok && setCheckoutOpen(null),
              )
            }
            onClose={() => setCheckoutOpen(null)}
          />
        )}

        {checkoutOpen === "cancel" && props.checkout && checkoutInfo !== null && (
          <ModalCancelCheckoutV4
            info={checkoutInfo}
            saving={props.checkout.saving}
            error={props.checkout.error}
            onConfirm={() => props.checkout?.onCancel((ok) => ok && setCheckoutOpen(null))}
            onClose={() => setCheckoutOpen(null)}
          />
        )}

        <div role="group" aria-label="ระดับสมาชิก" className="hv4-mem-card">
          <div className="hv4-mem-label">ระดับสมาชิก</div>
          {props.member === undefined ? (
            <div className="hv4-mem-value">-</div>
          ) : (
            <button
              type="button"
              className="hv4-mem-open"
              aria-haspopup="dialog"
              title="กดเพื่อดูรายละเอียดสิทธิสมาชิก"
              onClick={() => setMemberOpen(true)}
            >
              {props.member ? (
                <span className={`hv4-mem-tier hv4-mem-tier-${props.member.tierIndex}`}>
                  🏅 {props.member.tier}
                  <span className="hv4-mem-hint"> ▸</span>
                </span>
              ) : (
                <span className="hv4-mem-value">
                  ยังไม่เป็นสมาชิก
                  <span className="hv4-mem-hint"> ▸</span>
                </span>
              )}
            </button>
          )}
        </div>

        {memberOpen && props.member !== undefined && (
          <ModalV4 title="สิทธิสมาชิก" width={380} onClose={() => setMemberOpen(false)}>
            <MemberDetail member={props.member} />
          </ModalV4>
        )}

        {!!props.teamMessageErrors?.length && <AlertV4 tone="warn">โหลดข้อความถึงทีมบางส่วนไม่สำเร็จ กรุณาเปิดผู้รับบริการใหม่</AlertV4>}
        <PatientTeamFlagsV4 {...props} />

        <div className="hv4-p-rows">
          {rows.map((row) => (
            <div key={row.key} className="hv4-p-row">
              <span className="hv4-p-row-key">{row.key}</span>
              <span className="hv4-p-row-value">{row.value || "-"}</span>
            </div>
          ))}
        </div>

        <div className="hv4-info-card hv4-enc-card">
          <div className="hv4-enc-label">Encounter</div>
          <div className="hv4-enc-no">{props.encounter?.number || "-"}</div>
          <span className={`hv4-enc-status ${stage?.key ?? ""}`}>
            {stage?.label ?? props.encounter?.status_name}
          </span>
        </div>
      </aside>

      <nav aria-label="เมนูผู้ป่วย" className={`hv4-menu${collapsed ? " hv4-menu-collapsed" : ""}`}>
        <div className="hv4-menu-head">
          {collapsed ? null : <span>เมนู</span>}
          <button
            type="button"
            className="hv4-menu-collapse"
            aria-expanded={!collapsed}
            aria-label={collapsed ? "ขยายเมนู" : "ย่อเมนู"}
            title={collapsed ? "ขยายเมนู" : "ย่อเมนู · เพิ่มพื้นที่เนื้อหา"}
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? "»" : "«"}
          </button>
        </div>

        {pinnedItems.length > 0 && (
          <>
            <div className="hv4-menu-group">ปักหมุด</div>
            {pinnedItems.map(renderRow)}
          </>
        )}
        {groups.map((group) => (
          <Fragment key={group.label}>
            <div className="hv4-menu-group">{collapsed ? null : group.label}</div>
            {group.items.map(renderRow)}
          </Fragment>
        ))}
      </nav>

      <main
        className={mainClassNameOf(props.activeMenu)}
        onFocusCapture={presenceFocus}
        onChangeCapture={presenceTyping}
      >
        {props.presence?.bar}
        {props.children}
      </main>
    </div>
  );
};

export default PatientPanelV4;
